import { useEffect, useState } from 'react'
import { Outlet, useLocation, useNavigate } from '@tanstack/react-router'
import { Link } from '@/lib/router'
import {
  Activity,
  AlertTriangle,
  Banknote,
  Building2,
  CalendarPlus,
  ChevronDown,
  ClipboardCheck,
  ClipboardList,
  CreditCard,
  Eraser,
  FileBarChart,
  FileSearch,
  KeyRound,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Mail,
  MapPinned,
  PhoneCall,
  Settings,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Landmark,
  Upload,
  UserCheck,
  User as UserIcon,
  Users,
  Wallet,
} from 'lucide-react'

import { Dropdown, DropdownItem, DropdownLabel, DropdownSeparator } from '@/components/ui/dropdown'
import { useMe } from '@/features/auth/api'
import { resolveLogoUrl } from '@/lib/api/client'
import { useAuthStore } from '@/lib/auth/store'
import { useIdleLogout } from '@/lib/auth/useIdleLogout'
import { useRoleMatrix } from '@/features/admin/rolesApi'
import { useTenantTheme } from '@/lib/theme/useTenantTheme'
import { navPermissionCheck } from './navVisibility'
import { allRolesLabel, roleCaption } from './roleLabel'
import { cn } from '@/lib/utils'

// Each entry names the permission its destination endpoint enforces, so the
// sidebar shows a link only where the server would answer. Gate on
// PERMISSIONS, never on role names: roles are tenant-editable through the role
// editor, permissions are the stable contract. `permission` is the code the
// page's primary read requires — verified against each controller's
// @RequiresPermission, not guessed.
const navItems = [
  { to: '/' as const, label: 'Dashboard', icon: LayoutDashboard, hint: 'Portfolio overview', permission: 'DASHBOARD_VIEW' },
  // Telecalling. Gated on CALL_LOG, so these are the only Workspace entries a
  // telecaller sees — their shift plan and the console they work it from.
  { to: '/tele/queue' as const, label: 'My queue', icon: PhoneCall, hint: 'Callbacks, promises due, then priority', permission: 'CALL_LOG' },
  { to: '/cases' as const, label: 'Cases', icon: ListChecks, hint: 'Open delinquencies', permission: 'CASE_VIEW' },
  { to: '/allocations' as const, label: 'Allocations', icon: Users, hint: 'Bulk-assign cases to agents', permission: 'CASE_ALLOCATE' },
  { to: '/collections' as const, label: 'Collections', icon: Wallet, hint: 'Money in — cash and digital, by hierarchy', permission: 'RECOVERY_VIEW' },
  { to: '/settlements' as const, label: 'Settlements', icon: Landmark, hint: 'Gateway credits vs the payment ledger', permission: 'SETTLEMENT_VIEW' },
  { to: '/reconciliation' as const, label: 'Reconciliation', icon: AlertTriangle, hint: 'Drift findings and LMS posting', permission: ['RECON_FINDING_VIEW', 'COLLECTION_RECONCILE'] },
  { to: '/ingest' as const, label: 'Ingest', icon: Upload, hint: 'Daily file uploads', permission: 'INGESTION_UPLOAD' },
  { to: '/trips/approvals' as const, label: 'Visit approvals', icon: ClipboardCheck, hint: 'Mapped-manager inbox for field visits', permission: 'TRIP_REQUEST_APPROVE' },
  { to: '/trips/live' as const, label: 'Live visits', icon: Activity, hint: 'Every agent currently on the field, on one map', permission: 'TRIP_LIVE_VIEW' },
  { to: '/trips/review' as const, label: 'Visit review', icon: MapPinned, hint: 'Completed visit plans for the agents you can review', permission: 'TRIP_REVIEW_VIEW' },
  { to: '/trips/plan' as const, label: 'Plan visits', icon: CalendarPlus, hint: 'Submit an ad-hoc visit request or a weekly plan', permission: 'TRIP_REQUEST_CREATE' },
]

// MIS reports, one permission each: a tenant can give HR the attendance
// report without the PTP book.
const reportNavItems = [
  { to: '/reports/ptp' as const, label: 'PTP MIS', icon: FileBarChart, hint: 'Promises, what came in, and whether they were kept', permission: 'REPORT_PTP_MIS' },
  { to: '/reports/visits' as const, label: 'Visit MIS', icon: ClipboardList, hint: 'Every visit: timing, outcome and collection', permission: 'REPORT_VISIT_MIS' },
  { to: '/reports/attendance' as const, label: 'Attendance MIS', icon: UserCheck, hint: 'Sign-in, last action and visits per person per day', permission: 'REPORT_ATTENDANCE_MIS' },
]

const adminNavItems = [
  { to: '/admin/users' as const,    label: 'Users & roles',      icon: Shield,     hint: 'Provision agents, manage roles', permission: 'USER_VIEW' },
  { to: '/admin/org' as const,      label: 'Org structure',      icon: Building2,  hint: 'Branch / team / agent tree', permission: 'ORG_HIERARCHY_VIEW' },
  // Pre-launch only. Hidden unless admin.role_editor.enabled is set for the
  // tenant — the query answers { enabled: false } on the API's 404 rather
  // than erroring, so this simply does not render for customers.
  { to: '/admin/roles' as const,    label: 'Roles & permissions', icon: KeyRound,  hint: 'Tune the permission matrix', permission: 'ROLE_VIEW', flag: 'roleEditor' as const },
  { to: '/cash' as const,           label: 'Cash confirmation',  icon: Banknote,   hint: 'Reconcile deposited cash lots', permission: 'CASH_DEPOSIT_CONFIRM' },
  { to: '/admin/erasure' as const,  label: 'Right-to-erasure',   icon: Eraser,     hint: 'DPDP §12 erasure requests', permission: 'CUSTOMER_PII_ERASE' },
  { to: '/admin/audit' as const,    label: 'Audit log',          icon: FileSearch, hint: 'Every action, hash-chained', permission: 'AUDIT_VIEW' },
  { to: '/admin/audit/pii-access' as const, label: 'PII access',  icon: ShieldAlert, hint: 'Who looked at which customer', permission: 'PII_ACCESS_AUDIT_VIEW' },
  { to: '/admin/notifications' as const, label: 'Notifications', icon: Mail, hint: 'SMS + email templates and providers', permission: 'NOTIFICATION_CONFIG_MANAGE' },
  { to: '/settings' as const,       label: 'Settings',           icon: Settings,   hint: 'Conduct + reconciliation thresholds', permission: 'TENANT_CONFIG_MANAGE' },
  { to: '/settings/payment-types' as const, label: 'Payment types', icon: CreditCard, hint: 'Where money is collected to, and through which channels', permission: 'PAYMENT_TYPE_VIEW' },
]

// The platform tenant ("super") manages lenders and holds no lender data, so
// its staff see the console and their own admin pages, nothing else.
const PLATFORM_SLUG = 'super'
const platformNavItems = [
  { to: '/platform/dashboard' as const, label: 'Dashboard', icon: LayoutDashboard, hint: 'Adoption, usage, collections and health' },
  { to: '/platform/tenants' as const, label: 'Tenants', icon: Building2, hint: 'Create, enable and disable lenders' },
]
const platformAdminPaths: string[] = ['/admin/users', '/admin/audit']
const platformPaths = ['/platform', '/admin/users', '/admin/audit', '/profile']

export function AppLayout() {
  // Armed here and nowhere else: this component only mounts behind the auth
  // guard, so the timer cannot start on the login screen.
  useIdleLogout()

  const navigate = useNavigate()
  const location = useLocation()
  const { data: me } = useMe()
  const { data: roleEditor } = useRoleMatrix()

  // The rule lives in navVisibility.ts, with its own tests — it is cosmetic,
  // but getting it wrong is highly visible in both directions (a blank
  // sidebar, or a link the server then 403s).
  const can = navPermissionCheck(me)

  const isPlatform = me?.tenantSlug === PLATFORM_SLUG
  // An entry naming several permissions shows to a holder of any of them.
  const canAny = (p: string | readonly string[]) => (typeof p === 'string' ? can(p) : p.some(can))
  const visibleNavItems = isPlatform ? platformNavItems : navItems.filter((item) => canAny(item.permission))

  const visibleReportNavItems = isPlatform ? [] : reportNavItems.filter((item) => can(item.permission))

  // Flagged items vanish rather than 403-ing. `roleEditor` is undefined while
  // the query is in flight, so the item stays hidden until we know it is on —
  // same reasoning as above.
  const visibleAdminNavItems = isPlatform
    ? adminNavItems.filter((item) => platformAdminPaths.includes(item.to))
    : adminNavItems.filter(
        (item) => can(item.permission) && (!('flag' in item) || roleEditor?.enabled === true),
      )

  // Anywhere else (the lender dashboard is the default landing page) sends a
  // platform user to the console.
  useEffect(() => {
    if (isPlatform && !platformPaths.some((p) => location.pathname.startsWith(p))) {
      navigate({ to: '/platform/dashboard', replace: true })
    }
  }, [isPlatform, location.pathname, navigate])
  const logout = useAuthStore((s) => s.logout)
  useTenantTheme(me?.branding)

  function onLogout() {
    logout()
    navigate({ to: '/login' })
  }

  const initials = (me?.empId ?? '··').slice(0, 2).toUpperCase()
  const roleLine = roleCaption(me?.roles)
  const allRoles = allRolesLabel(me?.roles)

  return (
    <div className="min-h-screen bg-navy-50/40">
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/85 backdrop-blur">
        <div className="flex h-13 items-center justify-between px-4 lg:px-6">
          {/* Brand */}
          <div className="flex items-center gap-3">
            <BrandMark logoUrl={resolveLogoUrl(me?.branding?.logoUrl)} />
            <div className="flex items-baseline gap-2">
              <span className="text-sm font-semibold tracking-tight text-navy-900">Graviton</span>
              <span className="hidden text-2xs uppercase tracking-wider text-navy-600 sm:inline">
                Collections
              </span>
            </div>
            {(me?.tenantName || me?.tenantSlug) && (
              <span className="hidden items-center gap-1 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-2xs tracking-wide text-navy-700 sm:inline-flex">
                <Building2 className="h-3 w-3" />
                {me.tenantName || me.tenantSlug}
              </span>
            )}
          </div>

          {/* Right side: env + user menu */}
          <div className="flex items-center gap-3">
            <EnvPill />
            <Dropdown
              trigger={
                <>
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand text-2xs font-semibold text-brand-foreground">
                    {initials}
                  </span>
                  <span className="hidden text-left leading-tight sm:block">
                    <span className="block text-xs font-medium text-navy-900">{me?.empId ?? '—'}</span>
                    {roleLine && (
                      <span className="block text-2xs uppercase tracking-wider text-navy-600" title={allRoles}>
                        {roleLine}
                      </span>
                    )}
                  </span>
                  <ChevronDown className="h-3.5 w-3.5 text-navy-600" />
                </>
              }
            >
              <DropdownLabel>Signed in as</DropdownLabel>
              <div className="px-2 pb-2 text-sm">
                <div className="font-medium text-navy-900">{me?.empId ?? '—'}</div>
                <div className="truncate text-2xs text-muted-foreground">
                  <span className="text-navy-700">{me?.tenantName || me?.tenantSlug || '—'}</span>
                </div>
                {allRoles && <div className="text-2xs capitalize text-navy-600">{allRoles}</div>}
              </div>
              <DropdownSeparator />
              <DropdownItem
                icon={<UserIcon className="h-3.5 w-3.5" />}
                onSelect={() => navigate({ to: '/profile' })}
              >
                Profile
              </DropdownItem>
              <DropdownItem
                icon={<ShieldCheck className="h-3.5 w-3.5" />}
                onSelect={() => navigate({ to: '/profile/security' })}
              >
                Security
              </DropdownItem>
              <DropdownSeparator />
              <DropdownItem destructive icon={<LogOut className="h-3.5 w-3.5" />} onSelect={onLogout}>
                Sign out
              </DropdownItem>
            </Dropdown>
          </div>
        </div>
      </header>

      <div className="flex">
        <aside className="hidden min-h-[calc(100vh-3.25rem)] w-60 shrink-0 border-r border-slate-200 bg-white md:block">
          <nav className="flex flex-col gap-0.5 p-3">
            {visibleNavItems.length > 0 && (
              <p className="px-2 pb-1 pt-2 text-2xs font-semibold uppercase tracking-wider text-navy-600/80">
                {isPlatform ? 'Platform' : 'Workspace'}
              </p>
            )}
            {visibleNavItems.map((item) => {
              const active =
                item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to)
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={cn(
                    'group flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-all duration-fluid ease-fluid',
                    'motion-reduce:transition-none',
                    active
                      ? 'bg-brand-soft text-navy-900 font-medium shadow-soft'
                      : 'text-brand hover:bg-brand-soft/40 hover:text-navy-900',
                  )}
                >
                  <item.icon className={cn('h-4 w-4', active ? 'text-brand' : 'text-navy-500 group-hover:text-brand')} />
                  <span className="flex-1">{item.label}</span>
                  {active && <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-brand" />}
                </Link>
              )
            })}

            {visibleReportNavItems.length > 0 && (
              <p className="px-2 pb-1 pt-5 text-2xs font-semibold uppercase tracking-wider text-navy-600/80">
                Reports
              </p>
            )}
            {visibleReportNavItems.map((item) => {
              const active = location.pathname === item.to
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={cn(
                    'group flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-all duration-fluid ease-fluid',
                    'motion-reduce:transition-none',
                    active
                      ? 'bg-brand-soft text-navy-900 font-medium shadow-soft'
                      : 'text-brand hover:bg-brand-soft/40 hover:text-navy-900',
                  )}
                >
                  <item.icon className={cn('h-4 w-4', active ? 'text-brand' : 'text-navy-500 group-hover:text-brand')} />
                  <span className="flex-1">{item.label}</span>
                  {active && <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-brand" />}
                </Link>
              )
            })}

            {visibleAdminNavItems.length > 0 && (
              <p className="px-2 pb-1 pt-5 text-2xs font-semibold uppercase tracking-wider text-navy-600/80">
                Administration
              </p>
            )}
            {visibleAdminNavItems.map((item) => {
              // Each admin nav entry is its own page — match exactly so a
              // sub-route like /admin/audit/pii-access doesn't also light
              // up the /admin/audit parent.
              const active = location.pathname === item.to
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={cn(
                    'group flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-all duration-fluid ease-fluid',
                    'motion-reduce:transition-none',
                    active
                      ? 'bg-brand-soft text-navy-900 font-medium shadow-soft'
                      : 'text-brand hover:bg-brand-soft/40 hover:text-navy-900',
                  )}
                >
                  <item.icon className={cn('h-4 w-4', active ? 'text-brand' : 'text-navy-500 group-hover:text-brand')} />
                  <span className="flex-1">{item.label}</span>
                  {active && <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-brand" />}
                </Link>
              )
            })}

            {!isPlatform && (
            <div className="mt-6 rounded-md border border-slate-200 bg-navy-50/60 p-3">
              {me?.tenantTier === 'GRAVITON' ? (
                <>
                  <p className="text-2xs font-semibold uppercase tracking-wider text-navy-700">Graviton tier</p>
                  <p className="mt-1 text-xs leading-snug text-navy-800">
                    Loans, dues and payments arrive from your LMS.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-2xs font-semibold uppercase tracking-wider text-navy-700">Excel tier</p>
                  <p className="mt-1 text-xs leading-snug text-navy-800">
                    Upload <span className="font-medium">DPD dues</span> daily for fresh cases.
                  </p>
                </>
              )}
            </div>
            )}
          </nav>
        </aside>

        <main className="min-w-0 flex-1 p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

function BrandMark({ logoUrl }: { logoUrl?: string | null }) {
  const [failed, setFailed] = useState(false)

  if (logoUrl && !failed) {
    return (
      <img
        src={logoUrl}
        alt=""
        onError={() => setFailed(true)}
        className="h-7 w-7 rounded-md object-contain"
      />
    )
  }

  // Fallback: geometric glyph, retinted to the tenant's brand color.
  // Never a chubby pill.
  return (
    <span aria-hidden className="relative flex h-7 w-7 items-center justify-center rounded-md bg-brand shadow-soft">
      <span className="absolute inset-0 rounded-md ring-1 ring-inset ring-brand-foreground/30" />
      <span className="text-sm font-semibold tracking-tight text-brand-foreground">G</span>
    </span>
  )
}

function EnvPill() {
  const env = (import.meta.env.MODE ?? 'development').toLowerCase()
  if (env === 'production') return null
  return (
    <span className="hidden items-center gap-1 rounded border border-warning/30 bg-warning-soft px-1.5 py-0.5 text-2xs font-semibold uppercase tracking-wider text-warning sm:inline-flex">
      {env === 'development' ? 'Local' : env}
    </span>
  )
}
